import {
  Component,
  ElementRef,
  signal,
  computed,
  input,
  inject,
  DestroyRef,
  ContentChild,
  HostBinding,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';

@Component({
  selector: 'app-virtual-scroll',
  standalone: true,
  imports: [CommonModule, ScrollingModule],
  templateUrl: './virtual-scroll.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./virtual-scroll.component.scss'],
})
export class VirtualScrollComponent<T> {
  readonly items = input<T[]>([]);
  readonly size = input<'account' | 'wallet' | 'merchant' | 'ledger'>('wallet');

  readonly itemWidth = input<number>();
  readonly rowHeight = input<number>();
  readonly itemHeight = input<(item: T) => number>();
  readonly gapX = input<number>();
  readonly gapY = input<number>();
  readonly viewportHeight = input<string | number>();
  readonly emptyMessage = input<string>('');

  private readonly PRESETS: Record<
    'account' | 'wallet' | 'merchant' | 'ledger',
    { itemWidth: number; rowHeight: number; gapX: number; gapY: number; viewportHeight: string | number }
  > = {
    account: { itemWidth: 290, rowHeight: 145 + 15, gapX: 15, gapY: 15, viewportHeight: '100%' },
    wallet: { itemWidth: 249, rowHeight: 97 + 20, gapX: 20, gapY: 20, viewportHeight: 585 },
    merchant: { itemWidth: 500, rowHeight: 240 + 20, gapX: 20, gapY: 20, viewportHeight: '100%' },
    ledger: { itemWidth: 330, rowHeight: 140 + 20, gapX: 20, gapY: 20, viewportHeight: '100%' },
  };

  readonly layout = computed(() => {
    const preset = this.PRESETS[this.size()];
    const gapY = this.gapY() ?? preset.gapY;
    const itemHeight = this.itemHeight();
    const tallest =
      itemHeight && this.items().length
        ? this.items().reduce((max, item) => Math.max(max, itemHeight(item)), 0) + gapY
        : undefined;
    return {
      itemWidth: this.itemWidth() ?? preset.itemWidth,
      rowHeight: this.rowHeight() ?? tallest ?? preset.rowHeight,
      gapX: this.gapX() ?? preset.gapX,
      gapY,
      viewportHeight: this.viewportHeight() ?? preset.viewportHeight,
    };
  });

  @ContentChild(TemplateRef) readonly itemTemplate!: TemplateRef<{ $implicit: T }>;

  private host = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);

  private width = signal(0);
  readonly ready = computed(() => this.width() > 0);

  readonly itemsPerRow = computed(() => {
    const { itemWidth, gapX } = this.layout();
    return Math.max(1, Math.floor((this.width() + gapX) / (itemWidth + gapX)));
  });

  readonly rows = computed(() => {
    const result: T[][] = [];
    const data = this.items();
    const perRow = this.itemsPerRow();

    for (let i = 0; i < data.length; i += perRow) {
      result.push(data.slice(i, i + perRow));
    }
    return result;
  });

  readonly rowSize = computed(() => this.layout().rowHeight);
  readonly viewportHeightStyle = computed(() => {
    const max = this.layout().viewportHeight;
    const rowHeight = this.layout().rowHeight + 10;
    const rowsCount = this.rows().length;

    const contentHeight = rowsCount * rowHeight;
    if (typeof max === 'number') {
      return `${Math.min(contentHeight, max)}px`;
    }
    return max;
  });

  readonly emptyHeightStyle = computed(() => {
    const max = this.layout().viewportHeight;
    return typeof max === 'number' ? `${max}px` : max;
  });

  @HostBinding('style.--gap-x.px')
  get gapXStyle() {
    return this.layout().gapX;
  }

  @HostBinding('style.--gap-y.px')
  get gapYStyle() {
    return this.layout().gapY;
  }

  @HostBinding('style.--row-height.px')
  get rowHeightStyle() {
    return this.layout().rowHeight;
  }

  @HostBinding('style.--item-width.px')
  get itemWidthStyle() {
    return this.layout().itemWidth;
  }

  constructor() {
    const resize = new ResizeObserver((entries) => this.width.set(entries[0].contentRect.width));
    resize.observe(this.host.nativeElement);
    this.destroyRef.onDestroy(() => resize.disconnect());
  }

  trackRow = (i: number) => i;

  trackItem = (i: number, item: any) => item?.id ?? i;
}
